import { Typography } from "@mui/material";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { themePalette } from "../../theme";
import {
  ClinicalIndicationDashedDivider,
  ClinicalIndicationHeadingSection,
  ClinicalIndicationIcdDivider,
  SectionTitleText,
  StyledSectionHeadingClinical,
  StyledFormSecondaryLabelClinical,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { getNewReqTreatmentScreenNotes } from "./helpers";

import ClinicalIndicationTestPanel from "./NewReqClinicalIndicationTestPanel";
import ClinicalIndicationCodes from "./NewReqClinicalIndicationCodes";
import ClinicalIndicationAlert from "./NewReqClinicalIndicationAlert";
import { AneuploidyICDCodesClass } from "../../recoil/ICD10Codes/aneuploidyICDCodes";
import { CarrierICDCodesClass } from "../../recoil/ICD10Codes/carrierICDCodes";
import { FetalAntigenICDCodesClass } from "../../recoil/ICD10Codes/fetalAntigenICDCodes";
import { RhdICDCodesClass } from "../../recoil/ICD10Codes/rhdICDCodes";
import { NewRequisitionForm } from "../../recoil/types";
import {
  RBC_ANTIGENS,
  PLATELET_ANTIGENS,
  CLEARED_ANEUPLOIDY_ADD_ONS,
  hasAnyRbcAntigen,
  hasAnyPlateletAntigen,
} from "./fetalAntigenConstants";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { useMirrorRequisitionToCreateOrderState } from "./useMirrorRequisitionToCreateOrderState";

type ClinicalIndicationProps = {
  preflight: boolean;
  submittedRequisition?: NewRequisitionForm;
  isError?: boolean;
};

const ClinicalIndication = ({
  preflight = false,
  submittedRequisition,
  isError,
}: ClinicalIndicationProps) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm } = newRequisition;
  const effectiveIsError = isError ?? newRequisition.isError;
  const { enabled: isFetalAntigenExpansion } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenExpansion,
  );

  useEffect(() => {
    setNewRequisition((current) => {
      if (submittedRequisition) {
        return {
          ...current,
          preflight,
          isError,
          requisitionForm: submittedRequisition,
        };
      }
      // Only sync step flags — replacing the form would clobber Indication edits.
      return {
        ...current,
        preflight,
        isError,
      };
    });
  }, [isError, preflight]);

  useEffect(() => {
    if (!requisitionForm.dateOfBirth) {
      return;
    }
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        treatmentNotes: getNewReqTreatmentScreenNotes(current.requisitionForm),
      },
    }));
  }, [
    requisitionForm.aneuploidyIncludeRhd,
    requisitionForm.aneuploidyNIPT,
    requisitionForm.carrierScreen,
    requisitionForm.isRhdOnlyOrder,
  ]);

  useMirrorRequisitionToCreateOrderState({
    enabled: !preflight,
    requisitionForm,
  });

  useEffect(() => {
    if (
      (!requisitionForm.carrierScreen || requisitionForm.sex === "Male") &&
      requisitionForm.isFragileX
    ) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          isFragileX: false,
        },
      }));
    }
  }, [
    requisitionForm.isFragileX,
    requisitionForm.carrierScreen,
    requisitionForm.sex,
  ]);

  useEffect(() => {
    if (requisitionForm.carrierScreen) {
      return;
    }
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...new CarrierICDCodesClass(),
        isFragileX: false,
        isAcogPlus: false,
        expandedCarrierPanel: null,
        cfDNAOptOut: false,
      },
    }));
  }, [requisitionForm.carrierScreen]);

  useEffect(() => {
    if (requisitionForm.aneuploidyIncludeRhd) {
      return;
    }
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...new RhdICDCodesClass(),
      },
    }));
  }, [requisitionForm.aneuploidyIncludeRhd]);

  useEffect(() => {
    if (requisitionForm.isFetalAntigen) {
      return;
    }
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...new FetalAntigenICDCodesClass(),
      },
    }));
  }, [requisitionForm.isFetalAntigen]);

  useEffect(() => {
    if (requisitionForm.aneuploidyNIPT) {
      return;
    }
    const hasAneuploidyAddOns =
      requisitionForm.aneuploidyIncludeRhd ||
      requisitionForm.aneuploidyOptOutFetalSex ||
      requisitionForm.is22q ||
      requisitionForm.isFetalAntigen ||
      requisitionForm.isFetalAntigenRbc ||
      requisitionForm.isFetalAntigenPlatelet ||
      hasAnyRbcAntigen(requisitionForm) ||
      hasAnyPlateletAntigen(requisitionForm);

    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...new AneuploidyICDCodesClass(),
        ...(hasAneuploidyAddOns ? CLEARED_ANEUPLOIDY_ADD_ONS : {}),
      },
    }));
  }, [requisitionForm.aneuploidyNIPT]);

  // Auto-check RBC parent when any child antigen is selected (never auto-uncheck).
  useEffect(() => {
    const hasRbcAntigen = hasAnyRbcAntigen(requisitionForm);

    if (hasRbcAntigen && !requisitionForm.isFetalAntigenRbc) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          isFetalAntigenRbc: true,
        },
      }));
    }
  }, [
    requisitionForm.isFetalAntigenRbc,
    ...RBC_ANTIGENS.map((a) => requisitionForm[a.name]),
  ]);

  // Same for platelets when the expansion flag is on.
  useEffect(() => {
    if (!isFetalAntigenExpansion) return;

    const hasPlateletAntigen = hasAnyPlateletAntigen(requisitionForm);

    if (hasPlateletAntigen && !requisitionForm.isFetalAntigenPlatelet) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          isFetalAntigenPlatelet: true,
        },
      }));
    }
  }, [
    isFetalAntigenExpansion,
    requisitionForm.isFetalAntigenPlatelet,
    ...PLATELET_ANTIGENS.map((a) => requisitionForm[a.name]),
  ]);

  // Keep legacy isFetalAntigen in sync with RBC/platelet parents.
  useEffect(() => {
    if (
      !requisitionForm.isFetalAntigen &&
      (requisitionForm.isFetalAntigenRbc ||
        requisitionForm.isFetalAntigenPlatelet)
    ) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          isFetalAntigen: true,
        },
      }));
    }

    if (
      requisitionForm.isFetalAntigen &&
      !requisitionForm.isFetalAntigenRbc &&
      !requisitionForm.isFetalAntigenPlatelet
    ) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          isFetalAntigen: false,
        },
      }));
    }
  }, [
    requisitionForm.isFetalAntigen,
    requisitionForm.isFetalAntigenRbc,
    requisitionForm.isFetalAntigenPlatelet,
  ]);

  // RhD-only clears carrier + aneuploidy (and cannot apply for male patients).
  useEffect(() => {
    if (
      requisitionForm.isRhdOnlyOrder &&
      (requisitionForm.carrierScreen ||
        requisitionForm.aneuploidyNIPT ||
        requisitionForm.sex === "Male")
    ) {
      setNewRequisition((current) => ({
        ...current,
        requisitionForm: {
          ...current.requisitionForm,
          carrierScreen: false,
          aneuploidyNIPT: false,
          isRhdOnlyOrder:
            current.requisitionForm.sex === "Male"
              ? false
              : current.requisitionForm.isRhdOnlyOrder,
        },
      }));
    }
  }, [requisitionForm.isRhdOnlyOrder, requisitionForm.sex]);

  const noTestPanelSelected =
    !requisitionForm.aneuploidyNIPT &&
    !requisitionForm.carrierScreen &&
    !requisitionForm.isRhdOnlyOrder;
  const selectPanelsHeadingColor =
    newRequisition.isError && noTestPanelSelected
      ? themePalette.redDark
      : themePalette.greyDark;
  const showIcd10Section = !requisitionForm.isInternationalAccount;
  const showProviderAuth = !preflight;

  return (
    <>
      <ClinicalIndicationHeadingSection>
        <StyledSectionHeadingClinical
          $mt="24px"
          $mb="0"
          $color={selectPanelsHeadingColor}
        >
          Select test panel(s)*
        </StyledSectionHeadingClinical>
      </ClinicalIndicationHeadingSection>
      <ClinicalIndicationTestPanel />

      <ClinicalIndicationDashedDivider />

      {showIcd10Section && (
        <>
          <Typography variant="h6">
            <SectionTitleText>ICD-10 Code Selection*</SectionTitleText>
          </Typography>
          <br />
          <StyledFormSecondaryLabelClinical $paddingLeft={0}>
            Required to select at least one for each test panel. An ICD-10 code
            selected under one test panel might be used in another test
            panel&apos;s billing. The following codes are not exhaustive.
          </StyledFormSecondaryLabelClinical>
          <ClinicalIndicationCodes isError={effectiveIsError === true} />
          <ClinicalIndicationIcdDivider />
        </>
      )}

      {showProviderAuth && <ClinicalIndicationAlert />}
    </>
  );
};
export default ClinicalIndication;
